/**
 * A landing page - Figma `6440:35621`.
 *
 * The same sections as any other page, from a collection of its own, under `/lp/`. Why it
 * is a separate collection rather than a flag on `Page` is written at the top of
 * `apps/cms/src/landing-page.js`.
 *
 * **Never indexable, and not by asking politely.** `noindex, nofollow` is written
 * unconditionally - there is no field on the entry to forget - the CMS's `robots.txt`
 * disallows the whole `/lp/` prefix, and the sitemap is built from `Page` and cannot reach
 * these at all. Three independent things, because the cost of one of them failing is an
 * offer written for one client turning up in a search made by another.
 *
 * No canonical and no `hreflang` either: both are instructions to a crawler about a
 * document we are telling crawlers not to have.
 *
 * A missing landing page is the same plain "not found" as a missing page. An offer's URL
 * is the only thing protecting it, so "no such address" and "that offer has been taken
 * down" have to read identically.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SectionRenderer from '../sections/SectionRenderer';
import StatusPage from './StatusPage';
import { api, ApiError, type ApiPage } from '../lib/api';
import { applyHead } from '../lib/head';
import { useSite } from '../lib/site-context';

type State =
  | { kind: 'loading' }
  | { kind: 'landing'; page: ApiPage }
  | { kind: 'notFound' }
  | { kind: 'error' };

export default function LandingPage() {
  const { slug = '' } = useParams();
  const { region, locale, t } = useSite();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    api.landingPage(region.code, locale, slug)
      .then((page) => { if (!cancelled) setState({ kind: 'landing', page }); })
      .catch((error) => {
        if (cancelled) return;
        setState({ kind: error instanceof ApiError && error.status === 404 ? 'notFound' : 'error' });
      });

    return () => { cancelled = true; };
  }, [region.code, locale, slug]);

  useEffect(() => {
    if (state.kind !== 'landing') return;
    applyHead({ title: state.page.title, description: null, noindex: true });
  }, [state]);

  if (state.kind === 'loading') return <StatusPage title={t('page.loading')} body="" />;
  if (state.kind === 'notFound') return <StatusPage notFound title={t('page.notFound')} body={t('page.notFoundBody')} />;
  if (state.kind === 'error') return <StatusPage title={t('page.error')} body={t('page.errorBody')} />;

  return (
    <>
      {state.page.sections.map((section, i) => (
        <SectionRenderer key={`${section.__component}-${section.id}`} section={section} first={i === 0} />
      ))}
    </>
  );
}
